import React from 'react'
import styled from 'styled-components'
import { omit } from '@chakra-ui/utils'
import { mergeClassnames } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import { shouldShowFallbackImage, useImage } from './useImage'

/**
 * React component that renders an image with support
 * for fallbacks
 */
export const Image = React.forwardRef((props, ref) => {
  const {
    fallbackSrc,
    fallback,
    src,
    srcSet,
    align,
    fit,
    loading,
    ignoreFallback,
    crossOrigin,
    fallbackStrategy = 'beforeLoadOrError',
    referrerPolicy,
    htmlWidth,
    htmlHeight,
    alt,
    className,
    ...rest
  } = props

  const classnames = {
    placeholder: mergeClassnames('mote-image__placeholder', className),
    image: mergeClassnames('mote-image', className)
  }
  const providedFallback = fallbackSrc !== undefined || fallback !== undefined
  const shouldIgnoreFallbackImage =
    loading != null || ignoreFallback || !providedFallback

  const status = useImage({
    ...props,
    ignoreFallback: shouldIgnoreFallbackImage
  })

  const showFallbackImage = shouldShowFallbackImage(status, fallbackStrategy)

  const shared = {
    ref,
    alt,
    objectFit: fit,
    objectPosition: align,
    width: htmlWidth,
    height: htmlHeight,
    ...(shouldIgnoreFallbackImage ? rest : omit(rest, ['onError', 'onLoad']))
  }

  if (showFallbackImage) {
    if (fallback) return fallback

    return (
      <StyledImage
        className={classnames.placeholder}
        src={fallbackSrc}
        {...shared}
      />
    )
  }

  return (
    <StyledImage
      src={src}
      srcSet={srcSet}
      crossOrigin={crossOrigin}
      loading={loading}
      referrerPolicy={referrerPolicy}
      className={classnames.image}
      {...shared}
    />
  )
})

if (__DEV__) {
  Image.displayName = 'Image'
}

const StyledImage = styled.img`
  object-fit: ${({ objectFit }) => objectFit};
  object-position: ${({ objectPosition }) => objectPosition};
`
